import { logout } from 'actionCreators/auth';
import { setError } from 'actionCreators/error';
import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import { API_BASE_URL, BUILD_NUMBER } from 'settings';

import { getStore } from '../redux/store';
import tokenService from './token';

const timeout = async (timeInMilliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeInMilliseconds);
  });
};

const instance: AxiosInstance = axios.create();

instance.interceptors.request.use(
  async function (config): Promise<any> {
    try {
      const authToken = await tokenService.getToken();
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: authToken ? `Bearer ${authToken}` : '',
          FrontVersion: BUILD_NUMBER || 'local',
        },
      };
    } catch (error) {
      //Cancelling request
      return {
        headers: {},
        method: config.method,
        url: '',
      };
    }
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

const decorateConfig = (config: AxiosRequestConfig) => {
  const validateStatus = (status: number) => {
    return status >= 200 && status < 300;
  };
  config.validateStatus = config.validateStatus || validateStatus;
  return config;
};

const request = async (config: AxiosRequestConfig & { retries?: number }): Promise<any> => {
  config = decorateConfig(config);
  try {
    const response = await instance.request(config);

    if (response.headers['x-token']) {
      tokenService.updateToken(response.headers['x-token']);
    }

    return response;
  } catch (error) {
    const { dispatch } = getStore();

    if (!error.response && !config.validateStatus(0)) {
      dispatch(setError(504));
    } else if (error.response) {
      switch (error.response.status) {
        case 401:
          logout();
          break;
        case 503:
          //Retrying requests with 503 Service unavailable
          config.retries = (config.retries || 0) + 1;
          if (config.retries > 3) {
            break;
          }
          const retryTimeout = config.retries * (5_000 + Math.random() * 10_000);
          await timeout(retryTimeout);
          return request(config);
        default:
          break;
      }
    }

    return Promise.reject(error);
  }
};

export const makeRequest = (props: AxiosRequestConfig) =>
  request({
    ...props,
    url: props.url,
    baseURL: props.baseURL || API_BASE_URL,
  });

export const get = (props: AxiosRequestConfig) =>
  request({
    ...props,
    method: 'get',
    url: props.url,
    baseURL: props.baseURL || API_BASE_URL,
  });

export const post = (props: AxiosRequestConfig) =>
  request({
    ...props,
    method: 'post',
    url: props.url,
    baseURL: props.baseURL || API_BASE_URL,
  });

export const put = (props: AxiosRequestConfig) =>
  request({
    ...props,
    method: 'put',
    url: props.url,
    baseURL: props.baseURL || API_BASE_URL,
  });

export const del = (props: AxiosRequestConfig) =>
  request({
    ...props,
    method: 'delete',
    url: props.url,
    baseURL: props.baseURL || API_BASE_URL,
  });
